import React, { useMemo } from 'react'
import moment from 'moment'
import BedbankPackageTilePricing from './BedbankPackageTilePricing'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BedbankCancellationPolicy from './BedbankCancellationPolicy'
import BedbankOfferInclusions from './BedbankOfferInclusions'
import WalledContent from 'components/Common/WalledContent'
import AccommodationPackageTile from 'components/AccommodationKit/AccommodationPackageTile'
import AccommodationPackageTileContentArea from 'components/AccommodationKit/AccommodationPackageTileContentArea'
import AccommodationPackageTileDetails from 'components/AccommodationKit/AccommodationPackageTileDetails'
import BedbankPromotionDetails from './BedbankPromotionDetails'
import { useAppSelector } from 'hooks/reduxHooks'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { generateRoomOccupants, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { selectIsBedbankFlightBookingEnabled } from 'selectors/flightsSelectors'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import Group from 'components/utils/Group'
import { useBedbankSingleRoomAddToUserCartCallback, useBedbankSingleRoomBookNowCallback } from 'hooks/OfferPage/useBedbankBuyCallback'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'

const defaultRooms = [generateRoomOccupants()]

interface Props {
  packageView: App.BedbankOfferPackageView;
  rate: App.BedbankRate;
  checkInDate?: string;
  checkOutDate?: string;
  offer: App.BedbankOffer;
  rooms?: Array<App.Occupants>;
  optionNumber: number;
  flightOrigin?: App.Airport;
  isInitialPackageOption?: boolean;

  isSpoofed?: boolean;
  isLoggedIn?: boolean;
  originAirport?: App.Airport;
  flights?: boolean;

  /**
   * Unique ID for the list of rates this rate was shown under
   * Required for analytics
   */
  rateListId: string;
  /**
   * Position this room was in the list of rooms on the offer page
   * Required for analytics
   */
  roomPosition: number;
  /**
   * Unique ID for the list of rooms on the offer page
   * Required for analytics
   */
  roomListId: string;
  source?: string;
  showOptionLabel?: boolean;
}

function BedbankRoomOptionRate(props: Props) {
  const {
    offer,
    rooms = defaultRooms,
    checkInDate,
    checkOutDate,
    flightOrigin,
    roomListId,
    roomPosition,
    isInitialPackageOption,
    rateListId,
    optionNumber,
    packageView,
    rate,
    isSpoofed,
    showOptionLabel = true,
  } = props

  const isLoyaltyProgramFeatureFlagEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebt_hotel_loyalty)

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: flightOrigin?.code,
  })

  const isBedbankFlightBookingEnabled = useAppSelector(selectIsBedbankFlightBookingEnabled)
  const occupancyCount = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const flightsEnabled = !!offer.flights.flightsEnabled && isBedbankFlightBookingEnabled && occupancyCount < 10

  const { bundleRates, promotions } = packageView
  const bundleRate = bundleRates.find((bundleRate) => bundleRate.id === rate.id) ?? rate

  const departureTooEarly = useMemo(() => {
    const minDay = 1
    return !!checkInDate && moment(checkInDate).diff(moment(), 'days') < minDay
  }, [checkInDate])

  const onBook = useBedbankSingleRoomBookNowCallback({
    offer,
    packageView,
    checkInDate,
    checkOutDate,
    rooms,
    isSpoofed,
    rateListId,
    roomPosition,
    roomListId,
  })

  const onAddToUserCart = useBedbankSingleRoomAddToUserCartCallback({
    offer,
    packageView,
    checkInDate,
    checkOutDate,
    rooms,
    rateListId,
    roomPosition,
    roomListId,
  })

  const promotion = promotions[rate.promotionId ?? '']

  return <AccommodationPackageTile>
    <AccommodationPackageTileContentArea>
      <AccommodationPackageTileDetails
        packageNumber={showOptionLabel ? optionNumber : undefined}
        promotion={promotion}
        tagLineLabel={<>
          {rate.corporate && <ProductTypeLabel productType="corporate" />}
          {promotion && <ProductTypeLabel productType="limited_time_special" />}
          {(rate.inclusions.some((inclusion: App.OfferInclusion) => inclusion.description === 'Eligible for hotel loyalty points')
            && isLoyaltyProgramFeatureFlagEnabled) && <ProductTypeLabel productType="loyalty_program" />}
        </>}
      >
        {promotion && <BedbankPromotionDetails
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          offer={offer}
          promotion={promotion}
          rate={rate}
          showNonCancellation={!isInitialPackageOption}
        />}
        <Group direction="vertical" gap={8} noDisplayWhenEmpty>
          {!promotion && <BedbankCancellationPolicy
            rate={rate}
            offer={offer}
            showNonCancellation
          />}
        </Group>
      </AccommodationPackageTileDetails>
      <div>
        {promotion && <BedbankOfferInclusions
          content={promotion.rateInclusionsLong}
          inclusions={promotion.inclusions}
          maxHeight={180}
        />}
        {!promotion && <VerticalSpacer gap={4}>
          {rate.inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
        </VerticalSpacer>}
      </div>
    </AccommodationPackageTileContentArea>
    <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing}>
      <BedbankPackageTilePricing
        promotions={promotion}
        rate={rate}
        bundleRate={bundleRate}
        rooms={rooms}
        offer={offer}
        checkIn={checkInDate}
        checkOut={checkOutDate}
        flightPrice={flightPrice}
        flightsEnabled={flightsEnabled}
        departureTooEarly={departureTooEarly}
        flightOrigin={flightOrigin}
        ratePosition={optionNumber}
        rateListId={rateListId}
        roomPosition={roomPosition}
        roomListId={roomListId}
        onBookNow={onBook}
        onAddToUserCartNow={onAddToUserCart}
      />
    </WalledContent>
  </AccommodationPackageTile>
}

type MappedProps = Pick<Props,
  'checkInDate' |
  'checkOutDate' |
  'rooms' |
  'flightOrigin' |
  'isSpoofed' |
  'flights'
>

export default withOfferPageAndReduxState((offerState, appState): MappedProps => ({
  checkInDate: offerState.checkInDate,
  checkOutDate: getCheckoutDate(offerState),
  rooms: offerState.rooms,
  flightOrigin: offerState.airport,
  isSpoofed: appState.auth.account.isSpoofed,
}), BedbankRoomOptionRate)
